import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Calendar, AlertTriangle, CheckCircle2, Info, Clock, Zap, ArrowRight, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, HeadingLevel, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string {
  if (v >= 0) return `$${v.toLocaleString()}`;
  return `-$${Math.abs(v).toLocaleString()}`;
}

const TOOLTIP_STYLE = { contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }, itemStyle: { color: '#94a3b8' }, wrapperStyle: { outline: 'none' as const }, cursor: false };
const PRIORITY_COLORS: Record<string, string> = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#22c55e' };
const PRIORITY_BG: Record<string, string> = { critical: 'bg-red-500/10 border-l-red-500', high: 'bg-yellow-500/5 border-l-yellow-500', medium: 'bg-blue-500/5 border-l-blue-500', low: 'bg-green-500/5 border-l-green-500' };

export default function TaxPlanningPage() {
  const navigate = useNavigate();
  const [selectedClientId, setSelectedClientId] = useState('');

  // Firm-wide scan
  const { data: scanData, isLoading: scanLoading } = useQuery({
    queryKey: ['tax-planning-scan'],
    queryFn: async () => { const r = await apiClient.get('/tax-planning/scan'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  // Per-client plan
  const { data: planData, isLoading: planLoading } = useQuery({
    queryKey: ['tax-planning', selectedClientId],
    queryFn: async () => { const r = await apiClient.get(`/tax-planning/${selectedClientId}`); return r.data.data; },
    enabled: !!selectedClientId,
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-taxplan'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=500'); return r.data.data; },
  });
  const clients = (clientsData?.clients || clientsData || []).filter((c: any) => c.xeroOrgId || c.xero_org_id || c.qbo_realm_id || c.qboRealmId);

  const scanClients = scanData?.clients || [];
  const chartData = scanClients.filter((c: any) => c.totalSaving > 0).slice(0, 10).map((c: any) => ({
    name: c.clientName.length > 18 ? c.clientName.substring(0, 16) + '...' : c.clientName,
    saving: c.totalSaving,
  }));

  const plan = planData?.plan;
  const eofy = planData?.eofy || scanData;

  // Export plan as Word
  const exportPlan = async () => {
    if (!plan || !planData) return;
    const children: any[] = [
      new Paragraph({ children: [new TextRun({ text: 'Year-End Tax Planning Report', bold: true, size: 36, color: '2563eb' })], heading: HeadingLevel.HEADING_1, spacing: { after: 50 } }),
      new Paragraph({ children: [new TextRun({ text: planData.client.name, bold: true, size: 28 })], spacing: { after: 50 } }),
      new Paragraph({ children: [new TextRun({ text: `FY${planData.financials.fy} | ${planData.eofy.daysRemaining} days to 30 June | Generated ${new Date().toLocaleDateString('en-AU')}`, size: 20, color: '666666', italics: true })], spacing: { after: 300 } }),
    ];

    plan.items.forEach((item: any) => {
      children.push(new Paragraph({ children: [new TextRun({ text: `[${item.priority.toUpperCase()}] ${item.category}: ${item.action}`, bold: true, size: 24 })], heading: HeadingLevel.HEADING_2, spacing: { before: 200, after: 100 } }));
      children.push(new Paragraph({ children: [new TextRun({ text: item.description, size: 22 })], spacing: { after: 80 } }));
      if (item.estimatedSaving > 0) children.push(new Paragraph({ children: [new TextRun({ text: `Estimated Saving: ${fmtCurrency(item.estimatedSaving)}`, bold: true, size: 22, color: '22c55e' })], spacing: { after: 50 } }));
      children.push(new Paragraph({ children: [new TextRun({ text: `Deadline: ${item.deadline}`, size: 20, color: 'ef4444' })], spacing: { after: 50 } }));
      children.push(new Paragraph({ children: [new TextRun({ text: `ATO Rule: ${item.atoRule}`, size: 18, color: '666666', italics: true })], spacing: { after: 100 } }));
    });

    children.push(new Paragraph({ children: [new TextRun({ text: 'Generated by RoniDoc AI - Confidential', size: 16, color: '999999', italics: true })], alignment: AlignmentType.CENTER, spacing: { before: 400 } }));
    const doc = new Document({ sections: [{ properties: {}, children }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${planData.client.name.replace(/[^a-zA-Z0-9]/g, '_')}_Tax_Plan_${new Date().toISOString().split('T')[0]}.docx`);
    toast.success('Tax plan exported');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Calendar}
        title="Year-End Tax Planning"
        description={eofy?.daysToEOFY !== undefined
          ? (eofy.daysToEOFY > 0 ? `${eofy.daysToEOFY} days until 30 June ${eofy.eofyDate?.split('-')[0] || ''}` : 'Financial year has ended')
          : 'Generate per-client action plans before June 30'}
        iconColor="bg-orange-500/10 text-orange-500"
      />


      <Tabs defaultValue="scan">
        <TabsList>
          <TabsTrigger value="scan">All Clients</TabsTrigger>
          <TabsTrigger value="client">Per-Client Plan</TabsTrigger>
        </TabsList>

        {/* Firm-wide */}
        <TabsContent value="scan" className="space-y-4">
          {scanLoading ? (
            <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /><span className="ml-3 text-sm text-muted-foreground">Scanning all clients...</span></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Clients</p>
                  <p className="text-lg font-bold">{scanData?.totalClients || 0}</p>
                </CardContent></Card>
                <Card className="border-orange-500/30"><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-orange-500 uppercase">Days to EOFY</p>
                  <p className="text-lg font-bold text-orange-500">{scanData?.daysToEOFY || 0}</p>
                </CardContent></Card>
                <Card className="border-red-500/30"><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-red-500 uppercase">Critical Actions</p>
                  <p className="text-lg font-bold text-red-500">{scanData?.totalCritical || 0}</p>
                </CardContent></Card>
                <Card className="border-green-500/30"><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-green-500 uppercase">Total Potential Savings</p>
                  <p className="text-lg font-bold text-green-500">{fmtCurrency(scanData?.totalSaving || 0)}</p>
                </CardContent></Card>
              </div>

              {chartData.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Tax Savings by Client</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 30)}>
                      <BarChart data={chartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#999' }} width={110} />
                        <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => [fmtCurrency(v), 'Saving']} />
                        <Bar dataKey="saving" fill="#22c55e" radius={[0, 4, 4, 0]} activeBar={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Client Action Summary</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b bg-muted/30">
                      <th className="px-3 py-2 text-left font-medium">Client</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-right font-medium">Profit</th>
                      <th className="px-3 py-2 text-center font-medium">Actions</th>
                      <th className="px-3 py-2 text-center font-medium">Critical</th>
                      <th className="px-3 py-2 text-right font-medium">Est. Saving</th>
                    </tr></thead>
                    <tbody>
                      {scanClients.map((c: any, i: number) => (
                        <tr key={i} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/panel/compliance/tax-planning/${c.clientId}`)}>
                          <td className="px-3 py-2 font-medium">{c.clientName}</td>
                          <td className="px-3 py-2"><Badge variant="outline" className="text-[10px] capitalize">{c.entityType}</Badge></td>
                          <td className="px-3 py-2 text-right">{fmtCurrency(c.profit)}</td>
                          <td className="px-3 py-2 text-center">{c.actionItems}</td>
                          <td className="px-3 py-2 text-center">{c.criticalItems > 0 ? <span className="text-red-500 font-bold">{c.criticalItems}</span> : '-'}</td>
                          <td className="px-3 py-2 text-right text-green-500 font-medium">{c.totalSaving > 0 ? fmtCurrency(c.totalSaving) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Per-Client */}
        <TabsContent value="client" className="space-y-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium whitespace-nowrap">Select Client:</label>
                <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                  <SelectTrigger className="w-[350px]"><SelectValue placeholder="Choose a client..." /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {clients.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {plan && <Button size="sm" variant="outline" className="gap-1" onClick={exportPlan}><Download className="h-3.5 w-3.5" />Export Word</Button>}
              </div>
            </CardContent>
          </Card>

          {planLoading && <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}

          {plan && planData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Profit ({planData.financials.fy})</p>
                  <p className="text-sm font-bold">{fmtCurrency(planData.financials.profit)}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Entity Type</p>
                  <p className="text-sm font-bold capitalize">{planData.client.entityType}</p>
                </CardContent></Card>
                <Card><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-muted-foreground uppercase">Action Items</p>
                  <p className="text-sm font-bold">{plan.totalItems}</p>
                </CardContent></Card>
                <Card className="border-red-500/30"><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-red-500 uppercase">Critical</p>
                  <p className="text-sm font-bold text-red-500">{plan.criticalItems}</p>
                </CardContent></Card>
                <Card className="border-green-500/30"><CardContent className="pt-3 pb-3">
                  <p className="text-[10px] text-green-500 uppercase">Total Saving</p>
                  <p className="text-lg font-bold text-green-500">{fmtCurrency(plan.totalEstimatedSaving)}</p>
                </CardContent></Card>
              </div>

              {/* Action Items */}
              <div className="space-y-3">
                {plan.items.map((item: any, i: number) => (
                  <Card key={i} className={cn('border-l-4', PRIORITY_BG[item.priority])}>
                    <CardContent className="pt-3 pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge style={{ backgroundColor: PRIORITY_COLORS[item.priority] + '20', color: PRIORITY_COLORS[item.priority], border: 'none' }} className="text-[9px] uppercase">{item.priority}</Badge>
                            <Badge variant="outline" className="text-[9px]">{item.category}</Badge>
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="h-3 w-3" />{item.deadline}</span>
                          </div>
                          <p className="text-sm font-medium mb-1">{item.action}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
                          <p className="text-[10px] text-muted-foreground mt-1.5 italic">ATO: {item.atoRule}</p>
                        </div>
                        {item.estimatedSaving > 0 && (
                          <div className="text-right flex-shrink-0">
                            <p className="text-sm font-bold text-green-500">{fmtCurrency(item.estimatedSaving)}</p>
                            <p className="text-[10px] text-muted-foreground">est. saving</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
